import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaRobot, FaPlus, FaEdit, FaTrash, FaPlay, FaPause } from 'react-icons/fa';
import AdminLayout from '../../../components/admin/AdminLayout';
import PageHeader from '../../../components/common/PageHeader';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { useToast } from '../../../hooks/useToast';
import aiOrchestratorService from '../../../services/aiOrchestratorService';

const AIOrchestratorAgents = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [orchestrator, setOrchestrator] = useState(null);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const itemsPerPage = 10;

  const fetchOrchestrator = async () => {
    try {
      setIsLoading(true);
      const data = await aiOrchestratorService.getOrchestratorById(id);
      
      if (!data) {
        throw new Error('Orchestrator not found');
      }

      setOrchestrator(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching orchestrator:', error);
      setError(error.message || 'Failed to fetch orchestrator');
      showToast(error.message || 'Failed to fetch orchestrator', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgents = async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await aiOrchestratorService.getOrchestratorAgents(id, {
        page,
        limit: itemsPerPage,
        sortField,
        sortOrder,
        status: filters.status,
        search: filters.search
      });

      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }

      const { data, pagination } = response;
      setAgents(data || []);
      setTotalPages(pagination.pages || Math.ceil(pagination.total / pagination.limit));
    } catch (error) {
      console.error('Error fetching agents:', error);
      setError(error.message || 'Failed to load agents');
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrchestrator();
  }, [id]);

  useEffect(() => {
    fetchAgents(currentPage);
  }, [currentPage, sortField, sortOrder, filters]);

  const handleEdit = (agentId) => {
    navigate(`/admin/ai-orchestrator/${id}/agents/${agentId}/edit`);
  };

  const handleDelete = async (agentId) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      try {
        await aiOrchestratorService.deleteAgent(id, agentId);
        await fetchAgents(currentPage);
        showToast('Agent deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting agent:', error);
        showToast('Failed to delete agent', 'error');
      }
    }
  };

  const handleStatusChange = async (agentId, newStatus) => {
    try {
      await aiOrchestratorService.updateAgentStatus(id, agentId, newStatus);
      await fetchAgents(currentPage);
      showToast(`Agent ${newStatus} successfully`, 'success');
    } catch (error) {
      console.error('Error updating agent status:', error);
      showToast('Failed to update agent status', 'error');
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <PageHeader
          title={`${orchestrator.name} - Agents`}
          description="Manage and monitor your AI agents"
          icon={<FaRobot className="h-6 w-6 text-primary-500" />}
          actions={
            <div className="flex space-x-3">
              <button
                onClick={() => navigate(`/admin/ai-orchestrator/${id}`)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FaArrowLeft className="mr-2 -ml-1" />
                Back to Orchestrator
              </button>
              <button
                onClick={() => navigate(`/admin/ai-orchestrator/${id}/agents/new`)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FaPlus className="mr-2 -ml-1" />
                Add Agent
              </button>
            </div>
          }
        />

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <input
              type="text"
              name="search"
              placeholder="Search agents..."
              value={filters.search}
              onChange={handleFilterChange}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Agents List */}
        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner size="lg" text="Loading agents..." />
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      LLM Provider
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      LLM Model
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agents.map((agent) => (
                    <tr key={agent._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{agent.name}</div>
                        <div className="text-sm text-gray-500">{agent.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {agent.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          agent.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {agent.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {agent.configuration.llmProvider}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {agent.configuration.llmModel}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(agent._id)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <FaEdit className="inline-block" />
                        </button>
                        <button
                          onClick={() => handleDelete(agent._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FaTrash className="inline-block" />
                        </button>
                        {agent.status === 'active' ? (
                          <button
                            onClick={() => handleStatusChange(agent._id, 'inactive')}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            <FaPause className="inline-block" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(agent._id, 'active')}
                            className="text-green-600 hover:text-green-900"
                          >
                            <FaPlay className="inline-block" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex justify-center">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === page
                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AIOrchestratorAgents; 